import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUP_LOAD,
    ACTION_TRIALGROUP_LOAD_BY_ID,
    ACTION_TRIALGROUPS_LOAD_BY_GROUP_ID,
    ACTION_TRIALGROUPS_LOAD_BY_TRIAL_ID,
    ACTION_TRIALGROUP_LOAD_FAILURE,
    ACTION_TRIALGROUP_LOAD_SUCCESS,

    ACTION_TRIALGROUP_SAVE,
    ACTION_TRIALGROUP_SAVE_SUCCESS,
    ACTION_TRIALGROUP_SAVE_FAILURE,

    ACTION_TRIALGROUP_CREATE,
    ACTION_TRIALGROUP_CREATE_SUCCESS,
    ACTION_TRIALGROUP_CREATE_FAILURE,

    ACTION_TRIALGROUP_CLEAR,
    TrialGroupAction
} from '../actions/trialGroup';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupState {
    formData: Dtos.TrialGroup | Dtos.TrialGroup[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
}

const initialState: ITrialGroupState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState
};


function loading(state: ITrialGroupState, _isLoading: boolean): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialGroupState,
    formData: Dtos.TrialGroup | Dtos.TrialGroup[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function loadFailure(state: ITrialGroupState, responseStatus: Dtos.ResponseStatus): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITrialGroupState, propertiesToClear?: string[]): ITrialGroupState {

    if (!propertiesToClear) {
        const newState: ITrialGroupState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupState, _isSaving: boolean): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupState,
    formData: Dtos.TrialGroup | Dtos.TrialGroup[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupState, responseStatus: Dtos.ResponseStatus): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupState, _isCreating: boolean): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupState,
    formData: Dtos.TrialGroup,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupState, responseStatus: Dtos.ResponseStatus): ITrialGroupState {
    const newState: ITrialGroupState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialGroupReducer: Reducer<ITrialGroupState> = (state: ITrialGroupState = initialState, action: TrialGroupAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUP_LOAD:
        case ACTION_TRIALGROUP_LOAD_BY_ID:
        case ACTION_TRIALGROUPS_LOAD_BY_GROUP_ID:
            case ACTION_TRIALGROUPS_LOAD_BY_TRIAL_ID:
            return loading(state, true);
        case ACTION_TRIALGROUP_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUP_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUP_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUP_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUP_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUP_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUP_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUP_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUP_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
    }

    return state;
}

export default TrialGroupReducer;